/**
 * Zustand auth store — holds current user + tokens.
 *
 * O backend emite seu próprio JWT (access+refresh) baseado no Firebase ID token.
 * O access token é enviado em todas as chamadas a /v1/* via interceptor axios.
 */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export type UserRole =
  | "super_admin"
  | "admin"
  | "operator"
  | "financial"
  | "client"
  | "nerd"
  | "guest";

export interface AuthUser {
  id: string;
  email: string;
  email_verified: boolean;
  name: string | null;
  picture_url: string | null;
  role: UserRole;
  provider: string;
  is_active: boolean;
  is_blocked: boolean;
  onboarding_completed: boolean;
  last_login_at: string | null;
  created_at: string;
}

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  /** Epoch ms when access_token expires (computed locally). */
  expires_at: number;
}

export interface ImpersonationState {
  /** User original (admin) — restaurado ao sair da impersonation. */
  adminUser: AuthUser;
  adminTokens: AuthTokens;
}

interface AuthState {
  user: AuthUser | null;
  tokens: AuthTokens | null;
  impersonation: ImpersonationState | null;
  hydrated: boolean;
  setSession: (user: AuthUser, tokens: Omit<AuthTokens, "expires_at">) => void;
  setUser: (user: AuthUser) => void;
  startImpersonation: (target: AuthUser, targetTokens: Omit<AuthTokens, "expires_at">) => void;
  stopImpersonation: () => void;
  clear: () => void;
  markHydrated: () => void;
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tokens: null,
      impersonation: null,
      hydrated: false,
      setSession: (user, tokens) =>
        set({
          user,
          tokens: {
            ...tokens,
            expires_at: Date.now() + tokens.expires_in * 1000 - 30_000, // 30s skew
          },
        }),
      setUser: (user) => set({ user }),
      startImpersonation: (target, targetTokens) => {
        const { user, tokens } = get();
        if (!user || !tokens) return;
        set({
          user: target,
          tokens: {
            ...targetTokens,
            expires_at: Date.now() + targetTokens.expires_in * 1000 - 30_000,
          },
          impersonation: { adminUser: user, adminTokens: tokens },
        });
      },
      stopImpersonation: () => {
        const { impersonation } = get();
        if (!impersonation) return;
        set({
          user: impersonation.adminUser,
          tokens: impersonation.adminTokens,
          impersonation: null,
        });
      },
      clear: () => set({ user: null, tokens: null, impersonation: null }),
      markHydrated: () => set({ hydrated: true }),
    }),
    {
      name: "opnext-auth",
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        state?.markHydrated();
      },
      partialize: (s) => ({ user: s.user, tokens: s.tokens, impersonation: s.impersonation }),
    },
  ),
);

export function isAuthenticated(state = useAuth.getState()): boolean {
  return Boolean(state.user && state.tokens && state.tokens.expires_at > Date.now());
}

export function hasRole(
  role: UserRole | UserRole[],
  state = useAuth.getState(),
): boolean {
  if (!state.user) return false;
  const roles = Array.isArray(role) ? role : [role];
  return roles.includes(state.user.role);
}
